import { differenceInHours, format } from "date-fns";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import bigAssBattery from "../../assets/BigAssBattery.svg";
import cat950Icon from "../../assets/Cat950.png";
import densBattery from "../../assets/DensBattery.svg";
import MediumWheelLoaderIcon from "../../assets/MediumWheelLoader.jpg";
import pswIcon from "../../assets/PswIcon.svg";
import xosHubIcon from "../../assets/XOS_Hub.png";
import chargerIcon from "../../assets/charger.jpg";
import defaultMarkerIcon from "../../assets/defaultMarkerIcon.png";
import methanolReformerIcon from "../../assets/methanolReformer.png";
import unknownMachineIcon from "../../assets/unknownMachine.svg";
import { NodeIconColor, NodeIconType } from "../../components/CustomNodes/CustomNodeEnums";
import { SANKEY_NODE_WIDTH, SANKEY_SVG_WIDTH } from "../../components/SankeyDiagram/SankeyDiagram.interface";
import { MONTH_NAMES, formatTimestampWithTimezone } from "../../shared/timestampHelpers";
import { Activity } from "../../swagger/caasService-v1/index";
import { AssetStatus } from "../../swagger/caasService-v1/index";
// Constants for asset makes
const DENS_MAKE = "DENS";
const PSW_MAKE = "PSW";
const BAB_MAKE = "BAB";
// Constants for asset product family codes
const MDWL_PRODUCT_FAMILY = "MDWL";
// Constants for asset models
const MODEL_950GC = "950GC";
// Constants for asset source types
const ESS_TYPE = "ESS";
const MACHINE_TYPE = "MACHINE";
const CHARGER_TYPE = "CHARGER";
const CHARGING_STATION_TYPE = "CHARGING_STATION";
const ENERGY_SOURCE_TYPE = "ENERGY_SOURCE";
/**
 * @deprecated Use ENERGY_SOURCE_TYPE for the generic energy source asset category.
 * This constant is kept for backward compatibility with legacy naming.
 */
const METHANOL_REFORMER_TYPE = ENERGY_SOURCE_TYPE;
const GHOST_TYPE = "GHOST";
/**
 * Checks if a timestamp string is in time-only format (HH:mm:ss).
 *
 * Time-only format is used by some projects (e.g., Malaga) where the API returns
 * only the time portion without a date (e.g., "15:00:00", "07:00:00").
 *
 * @param timestamp - The timestamp string to check
 * @returns true if the timestamp matches the time-only format (HH:mm:ss), false otherwise
 *
 * @example
 * isTimeOnlyTimestamp("15:00:00") // true
 * isTimeOnlyTimestamp("2025-12-18T15:00:00") // false
 * isTimeOnlyTimestamp("15:00") // false (missing seconds)
 * isTimeOnlyTimestamp(null) // false
 */
export const isTimeOnlyTimestamp = (timestamp) => {
    if (!timestamp)
        return false;
    return /^\d{2}:\d{2}:\d{2}$/.test(timestamp);
};
// Asset name mappings for special cases (e.g., DENS assets with non-standard naming)
const ASSET_NAME_MAPPINGS = {
    E_NOMAD_01: "E_N_1",
    E_NOMAD_02: "E_N_2",
    "PH10-24016": "PH16",
    "PH10-24019": "PH19"
};
const UNKNOWN_ASSET_PREFIX = "Unknown Asset";
const formatUnknownAssetName = (assetName) => {
    if (assetName?.toLocaleLowerCase()?.startsWith(UNKNOWN_ASSET_PREFIX?.toLocaleLowerCase())) {
        const suffix = assetName?.substring(UNKNOWN_ASSET_PREFIX.length)?.trim();
        return suffix;
    }
    return null;
};
/**
 * Returns the appropriate battery icon based on the make field
 */
export const getBatteryIconByMake = (make) => {
    switch (make) {
        case PSW_MAKE:
            return pswIcon;
        case DENS_MAKE:
            return densBattery;
        case BAB_MAKE:
        default:
            return bigAssBattery;
    }
};
/**
 * Returns the display name for a battery make
 */
export const getBatteryMakeDisplayName = (make) => {
    switch (make) {
        case PSW_MAKE:
            return "Pon Energy Rental";
        case BAB_MAKE:
            return "Big Ass Battery";
        case DENS_MAKE:
            return "DENS Battery";
        default:
            return make;
    }
};
export const getNetworkNodes = (assetData, projectName, projectId, projectDetails, assetSoCPowerTrendsDetails, tooltipShowOnlyAssetName, projectTimezone) => {
    const networkNodes = [];
    const ewx = 450;
    let ewy = 100;
    const etx = 670;
    let ety = 150;
    const esx = 850;
    let esy = 100;
    const msx = 1050;
    let msy = 100;
    const chargeStationPositionX = 250;
    let chargeStationPositionY = 80;
    const CHARGE_STATION_ADD_VALUE_Y = 270;
    const siteESSPos = new Map();
    const sourceESSPos = new Map();
    const siteMachinePos = [];
    const sortedData = sortNetworkData(assetData || []);
    const chargeStationData = [];
    sortedData.forEach((data) => {
        let xc;
        let yc;
        let assetIcon;
        const ADD_VALUE = 120;
        // when activity is in transporting we need to show the position of the node under transit
        if (data?.activity === "Transporting") {
            xc = etx;
            yc = ety;
            ety = ety + ADD_VALUE;
            assetIcon =
                data?.assetSourceType?.toUpperCase() === MACHINE_TYPE
                    ? NodeIconType.EXCAVATOR
                    : NodeIconType.TRUCKWITHESS;
        }
        else {
            if (data?.assetSourceType?.toUpperCase() === ESS_TYPE ||
                data?.assetSourceType?.toUpperCase() === CHARGER_TYPE ||
                (data?.assetSourceType?.toUpperCase() === MACHINE_TYPE &&
                    data?.assetTransitDirection?.toUpperCase() === AssetTransitDirection.AT_SOURCE)) {
                if (data?.assetTransitDirection?.toUpperCase() === AssetTransitDirection.AT_SITE) {
                    xc = esx;
                    yc = esy;
                    esy = esy + ADD_VALUE;
                    siteESSPos.set(data.assetId, yc);
                    assetIcon =
                        data?.assetSourceType?.toUpperCase() === MACHINE_TYPE
                            ? NodeIconType.EXCAVATOR
                            : NodeIconType.ESS;
                }
                else {
                    xc = ewx;
                    yc = ewy;
                    ewy = ewy + ADD_VALUE;
                    sourceESSPos.set(data.assetId, yc);
                    assetIcon =
                        data?.assetSourceType?.toUpperCase() === MACHINE_TYPE
                            ? NodeIconType.EXCAVATOR
                            : NodeIconType.ESS;
                }
            }
            if (data?.assetSourceType?.toUpperCase() === MACHINE_TYPE &&
                data?.assetTransitDirection?.toUpperCase() === AssetTransitDirection.AT_SITE) {
                xc = msx;
                let essY = getChargingSourcePosition(data.chargingSource, siteESSPos);
                if (essY !== null) {
                    while (siteMachinePos.includes(essY)) {
                        essY = essY + ADD_VALUE;
                    }
                    yc = essY;
                }
                else {
                    while (siteMachinePos.includes(msy)) {
                        msy = msy + ADD_VALUE;
                    }
                    yc = msy;
                    msy = msy + ADD_VALUE;
                }
                siteMachinePos.push(yc);
                assetIcon = NodeIconType.EXCAVATOR;
            }
        }
        if (projectDetails?.find((item) => item?.assetId === data?.assetId && item?.assetMetadata?.productFamily?.code === "MDWL")) {
            assetIcon = NodeIconType.WHEELLOADER;
        }
        if (data?.assetSourceType?.toUpperCase() === CHARGER_TYPE) {
            if (data?.activity === "Idle") {
                assetIcon = NodeIconType.IDLEMECCHARGER;
            }
            else {
                assetIcon = NodeIconType.CHARGEMECCHARGER;
            }
        }
        if (data?.assetSourceType?.toUpperCase() === METHANOL_REFORMER_TYPE) {
            if (data?.activity === "Transporting") {
                xc = etx;
                yc = ety;
                ety = ety + ADD_VALUE;
            }
            else if (data?.assetTransitDirection?.toUpperCase() === AssetTransitDirection.AT_SITE) {
                xc = esx;
                yc = esy;
                esy = esy + ADD_VALUE;
            }
            else {
                // Default: source column (AtSource or no direction set)
                xc = ewx;
                yc = ewy;
                ewy = ewy + ADD_VALUE;
            }
            assetIcon = NodeIconType.METHANOL_REFORMER;
        }
        if (data?.assetSourceType?.toUpperCase() === GHOST_TYPE) {
            if ((data?.assetTransitDirection?.toUpperCase() === AssetTransitDirection.AT_SOURCE ||
                data?.assetTransitDirection?.toUpperCase() === AssetTransitDirection.TOWARDS_SOURCE) &&
                data?.chargingSource) {
                const sourceY = getChargingSourcePosition(data.chargingSource, sourceESSPos);
                if (sourceY !== null) {
                    xc = ewx + 93;
                    yc = sourceY;
                }
                else {
                    xc = ewx + 100;
                    yc = ewy;
                    ewy = ewy + ADD_VALUE;
                }
            }
            else if (data?.assetTransitDirection?.toUpperCase() === AssetTransitDirection.AT_SOURCE ||
                data?.assetTransitDirection?.toUpperCase() === AssetTransitDirection.TOWARDS_SOURCE) {
                xc = ewx;
                yc = ewy;
                ewy = ewy + ADD_VALUE;
            }
            else {
                xc = msx;
                let ghostEssY = data.chargingSource ? getChargingSourcePosition(data.chargingSource, siteESSPos) : null;
                if (ghostEssY === null) {
                    while (siteMachinePos.includes(msy)) {
                        msy = msy + ADD_VALUE;
                    }
                    yc = msy;
                    msy = msy + ADD_VALUE;
                }
                else {
                    while (siteMachinePos.includes(ghostEssY)) {
                        ghostEssY = ghostEssY + ADD_VALUE;
                    }
                    yc = ghostEssY;
                }
                siteMachinePos.push(yc);
            }
            assetIcon = NodeIconType.GHOST_MACHINE;
        }
        if (data?.assetSourceType?.toUpperCase() === CHARGING_STATION_TYPE) {
            chargeStationData.push(data);
        }
        data?.assetSourceType?.toUpperCase() !== CHARGING_STATION_TYPE &&
            networkNodes.push({
                data: {
                    activity: data?.activity,
                    assetTransitDirection: projectName === "Hansweert" && projectId === "308a84bb-fc3a-442e-92c9-5508086385f0"
                        ? undefined
                        : data?.assetTransitDirection,
                    hasSource: data?.assetSourceType === ESS_TYPE ||
                        data?.assetSourceType?.toUpperCase() === CHARGER_TYPE ||
                        data?.assetSourceType?.toUpperCase() === METHANOL_REFORMER_TYPE,
                    hasTarget: !!data?.chargingSource,
                    iconColor: tooltipShowOnlyAssetName
                        ? // Replay mode: empty string means no recorded activity at this slider position
                            // → show Not Reporting grey. Any real activity (including Idle) → use its colour.
                            data?.activity
                                ? getNodeIconColor(data.activity, data?.assetStatus)
                                : NodeIconColor.NOT_REPORTING
                        : // Live mode: use staleness check as before
                            (data?.location?.lastReportedTime
                                ? isAssetReporting(data.location.lastReportedTime, projectTimezone)
                                : !!data?.activity)
                                ? getNodeIconColor(data?.activity, data?.assetStatus)
                                : NodeIconColor.NOT_REPORTING,
                    iconType: assetIcon,
                    label: getAssetName(data?.assetName, data?.assetSourceType),
                    selected: false,
                    toolTip: {
                        address: data?.location?.streetAddress,
                        assetDetailsLink: "/asset-page-details/" + data?.assetId,
                        assetEnergy: data?.assetEnergy,
                        assetLatestPower: data?.assetLatestPower,
                        assetName: data?.assetName,
                        chargeTime: data?.estimatedRemainingChargeTime,
                        distance: data?.odometerReading,
                        distanceUom: "Miles",
                        hours: data?.lifetimeRunningHours,
                        isReporting: tooltipShowOnlyAssetName
                            ? !!data?.activity
                            : data?.location?.lastReportedTime
                                ? isAssetReporting(data.location.lastReportedTime, projectTimezone)
                                : !!data?.activity,
                        powerTrend: trendData(assetSoCPowerTrendsDetails, data.assetId, "power"),
                        soc: data?.soc,
                        socTrend: trendData(assetSoCPowerTrendsDetails, data.assetId, "soc"),
                        state: data?.activity,
                        tooltipShowOnlyAssetName
                    }
                },
                id: data?.assetId,
                position: { x: xc, y: yc },
                type: "assetNode"
            });
    });
    if (networkNodes.length !== 0) {
        networkNodes.push({
            data: {},
            id: "Power",
            position: { x: 100, y: 288 },
            type: "utilityPowerNode"
        });
        const getChargeStationLabel = (assetName) => {
            if (projectName === "Amsterdam" && projectId === "d85bd393-6cc3-4461-bcf0-78365d5bbf12") {
                return "Grid Connection";
            }
            const words = assetName?.replace(/_/g, " ").split(" ");
            const capitalizedWords = words?.map((word) => word.charAt(0).toUpperCase() + word.slice(1));
            return capitalizedWords.join(" ");
        };
        chargeStationData.forEach((data) => {
            networkNodes.push({
                data: { label: getChargeStationLabel(data?.assetName) },
                id: data.assetId,
                position: { x: chargeStationPositionX, y: chargeStationPositionY },
                type: "chargeStationNode"
            });
            chargeStationPositionY = chargeStationPositionY + CHARGE_STATION_ADD_VALUE_Y;
        });
    }
    return networkNodes;
};
const trendData = (assetSoCPowerTrendsDetails, assetId, trend) => {
    const values = [];
    assetSoCPowerTrendsDetails?.forEach((assetTrend) => {
        if (assetTrend.assetId === assetId && trend === "soc") {
            assetTrend.trends?.forEach((trend) => {
                if (trend.soc !== undefined) {
                    values.push(trend.soc);
                }
            });
        }
        else if (assetTrend.assetId === assetId && trend === "power") {
            assetTrend.trends?.forEach((trend) => {
                if (trend.power !== undefined) {
                    values.push(trend.power);
                }
            });
        }
    });
    return values;
};
const sortNetworkData = (assetData) => {
    const essData = [];
    const methanolData = [];
    const chargingMachines = [];
    const otherData = [];
    assetData.forEach((data) => {
        if (data.assetSourceType === ESS_TYPE || data?.assetSourceType?.toUpperCase() === CHARGER_TYPE) {
            essData.push(data);
        }
        else if (data?.assetSourceType?.toUpperCase() === METHANOL_REFORMER_TYPE) {
            // ENERGY_SOURCE (methanol reformer) assets go after ESS so they
            // stack below ESS nodes in the source column. Prod sort order unchanged.
            methanolData.push(data);
        }
        else if (data.assetSourceType?.toUpperCase() === MACHINE_TYPE &&
            data.chargingSource !== null &&
            data.chargingSource.toUpperCase() !== "OTHERS") {
            chargingMachines.push(data);
        }
        else if (data.assetSourceType?.toUpperCase() === GHOST_TYPE &&
            data.chargingSource !== null &&
            data.chargingSource !== undefined) {
            chargingMachines.push(data);
        }
        else {
            otherData.push(data);
        }
    });
    return essData.concat(methanolData).concat(chargingMachines).concat(otherData);
};
const getChargingSourcePosition = (source, essPos) => {
    let pos = null;
    if (source === null || source.toUpperCase() === "OTHERS")
        return null;
    essPos.forEach((value, key) => {
        if (source === key) {
            pos = value;
        }
    });
    return pos;
};
export const getNodeIconColor = (state, assetStatus) => {
    // Methanol-reformer specific states come from assetStatus, not activity.
    // Check status first so it takes priority over any fallback activity value.
    if (assetStatus === AssetStatus.FAULT) {
        return NodeIconColor.FAULTED;
    }
    if (assetStatus === AssetStatus.OFF) {
        return NodeIconColor.OFF;
    }
    // Charger-specific statuses (may arrive via assetStatus or activity fallback).
    // Normalise to lowercase for case-insensitive comparison.
    const chargerStatus = (assetStatus || state || "").toLowerCase();
    if (chargerStatus === "available") {
        return NodeIconColor.CHARGING; // green — charger is ready/available
    }
    if (chargerStatus === "occupied") {
        return NodeIconColor.IDLE; // blue — charger is in use
    }
    if (chargerStatus === "inoperable") {
        return NodeIconColor.FAULTED; // dark red — charger is out of service
    }
    if (state === Activity.CHARGING || state === AssetStatus.AC_CHARGING || state === AssetStatus.DC_CHARGING) {
        return NodeIconColor.CHARGING;
    }
    if (state === Activity.DISCHARGING) {
        return NodeIconColor.DISCHARGING;
    }
    if (state === Activity.IDLE) {
        return NodeIconColor.IDLE;
    }
    if (state === Activity.TRANSPORTING) {
        return NodeIconColor.TRANSPORTING;
    }
    if (state === AssetStatus.TRICKLE) {
        return NodeIconColor.TRICKLE;
    }
    if (state === AssetStatus.SELF_DISCHARGE) {
        return NodeIconColor.SELF_DISCHARGE;
    }
    if (state === AssetStatus.WORKING) {
        return NodeIconColor.WORKING;
    }
    return NodeIconColor.INACTIVE;
};
/**
 * Determines if an asset is currently reporting based on its last reported timestamp.
 *
 * CALCULATION FUNCTION - Uses fromZonedTime approach (see TIMEZONE_ARCHITECTURE.md)
 *
 * NOTE: The API provides timestamps already in the project's local timezone.
 * This function needs accurate UTC milliseconds to compare with Date.now().
 *
 * Why fromZonedTime instead of Z-appending:
 *   - Z-appending would treat "20:46 London" as "20:46 UTC" (WRONG)
 *   - fromZonedTime correctly interprets "20:46 London" and converts to UTC
 *   - This ensures all users get the same "REPORTING" vs "NOT REPORTING" status
 *
 * @param time - Timestamp string in project's local timezone (e.g., "2025-12-17T20:46:00.000")
 * @param projectTimezone - IANA timezone name (e.g., "Europe/London", "Europe/Amsterdam")
 * @returns true if the asset reported within the last 18 hours, false otherwise
 */
const isAssetReporting = (time, projectTimezone) => {
    const MILLI_SECONDS_IN_HOUR = 3600000;
    const HOURS = 18;
    if (!time)
        return false;
    try {
        // Special handling for time-only format (e.g., "19:00:00")
        // Append today's date in the project's timezone to construct a full timestamp
        if (isTimeOnlyTimestamp(time)) {
            if (!projectTimezone || !isValidIANATimezone(projectTimezone)) {
                // Cannot determine staleness without valid timezone, assume reporting
                return true;
            }
            // Get today's date in the project's timezone (e.g., "2025-12-18" in Europe/Amsterdam)
            const todayInProjectTz = formatInTimeZone(new Date(), projectTimezone, "yyyy-MM-dd");
            // Construct full timestamp: "2025-12-18T19:00:00"
            const fullTimestamp = `${todayInProjectTz}T${time}`;
            // Parse as project timezone and convert to UTC for comparison
            const utcDate = fromZonedTime(fullTimestamp, projectTimezone);
            const machineTime = utcDate.getTime();
            if (isNaN(machineTime)) {
                return false;
            }
            const milliDiff = Date.now() - machineTime;
            const hoursDelta = milliDiff / MILLI_SECONDS_IN_HOUR;
            return hoursDelta >= 0 && hoursDelta < HOURS;
        }
        // API provides timestamps in project's local timezone (e.g., "2025-12-17T20:46:00.000" = 20:46 London time)
        // We interpret this timestamp AS IF it's in the project's timezone, then convert to UTC for comparison
        if (projectTimezone && isValidIANATimezone(projectTimezone)) {
            // Parse the timestamp string as a date in the project's timezone, convert to UTC
            // fromZonedTime interprets a plain date string as if it's in the specified timezone
            // Returns a Date object with correct UTC milliseconds for reliable comparison
            const utcDate = fromZonedTime(time, projectTimezone);
            const machineTime = utcDate.getTime();
            if (isNaN(machineTime)) {
                return false;
            }
            const milliDiff = Date.now() - machineTime;
            const hoursDelta = milliDiff / MILLI_SECONDS_IN_HOUR;
            // Allow up to MAX_UTC_OFFSET_HOURS negative to guard against any clock skew
            // or edge cases where fromZonedTime produces a slightly "future" result.
            const MAX_UTC_OFFSET_HOURS = 14;
            return hoursDelta >= -MAX_UTC_OFFSET_HOURS && hoursDelta < HOURS;
        }
        else {
            // Fallback: treat as UTC if no valid IANA timezone provided.
            // Timestamps are in project local time, not UTC. Appending "Z" makes them
            // appear up to MAX_UTC_OFFSET_HOURS ahead of real UTC for UTC+ projects
            // (e.g. Amsterdam UTC+2 means local 07:00 looks like 07:00Z but actual UTC
            // is 05:00, giving hourDiff = -2). Allow up to 14 hours negative (the world's
            // maximum UTC+ offset) so these assets are not falsely marked Not Reporting.
            const MAX_UTC_OFFSET_HOURS = 14;
            const timeAsUTC = time.endsWith("Z") ? time : `${time}Z`;
            const machineTime = new Date(timeAsUTC).getTime();
            if (isNaN(machineTime)) {
                return false;
            }
            const milliDiff = Date.now() - machineTime;
            const hoursDelta = milliDiff / MILLI_SECONDS_IN_HOUR;
            return hoursDelta >= -MAX_UTC_OFFSET_HOURS && hoursDelta < HOURS;
        }
    }
    catch (error) {
        return false;
    }
};
/**
 * Validates if a timezone string is a valid IANA timezone name.
 * Rejects offset formats like "UTC+01:00" or "+01:00" which cannot be used with date-fns-tz.
 *
 * @param timezone - The timezone string to validate
 * @returns true if the timezone is a valid IANA timezone name, false otherwise
 */
const isValidIANATimezone = (timezone) => {
    if (!timezone)
        return false;
    // Reject offset formats like "UTC+01:00", "+01:00", "UTC+1", "+1"
    if (timezone.match(/^(UTC)?[+-]\d{1,2}(:\d{2})?$/)) {
        console.warn(`Invalid timezone format: "${timezone}" appears to be a UTC offset, not an IANA timezone name. ` +
            `Expected format like "Europe/Amsterdam" or "America/New_York"`);
        return false;
    }
    // Valid IANA timezones contain "/" (e.g., "Europe/Amsterdam", "America/New_York")
    // or are short forms like "UTC", "GMT"
    if (timezone === "UTC" || timezone === "GMT") {
        return true;
    }
    if (!timezone.includes("/")) {
        console.warn(`Potentially invalid timezone format: "${timezone}". Expected IANA timezone name like "Europe/Amsterdam"`);
        return false;
    }
    return true;
};
export const getNetworkEdges = (assetData) => {
    const networkEdges = [];
    const edgeMap = new Map();
    const ghostAssetIds = new Set();
    let n = 0;
    assetData.forEach((data) => {
        if (data?.chargingSource) {
            edgeMap.set(data.assetId, data.chargingSource);
        }
        if (data?.assetSourceType?.toUpperCase() === CHARGING_STATION_TYPE) {
            edgeMap.set(data.assetId, "Power");
        }
        if (data?.assetSourceType?.toUpperCase() === GHOST_TYPE && data?.chargingSource) {
            ghostAssetIds.add(data.assetId);
        }
    });
    for (const [key, value] of edgeMap.entries()) {
        const isParallelConnection = ghostAssetIds.has(key);
        networkEdges.push({
            id: "e" + n,
            source: value,
            style: { strokeWidth: 3 },
            target: key,
            type: isParallelConnection ? "straight" : undefined
        });
        n++;
    }
    return networkEdges;
};
export const getAssetName = (assetName, assetSourceType) => {
    // Handle specific asset name mappings using shared constant
    if (ASSET_NAME_MAPPINGS[assetName]) {
        return ASSET_NAME_MAPPINGS[assetName];
    }
    if (assetSourceType?.toUpperCase() === GHOST_TYPE) {
        const unknownFormatted = formatUnknownAssetName(assetName);
        if (unknownFormatted)
            return unknownFormatted;
        return assetName ? assetName.replace("Unknown Machine", "").trim() : "";
    }
    const FIRST_POSITION = 0;
    const MACHINE_LAST_POSITION = 5;
    const COMMON_LAST_POSITION = 3;
    const LAST_POSITION = assetSourceType?.toUpperCase() === MACHINE_TYPE ? MACHINE_LAST_POSITION : COMMON_LAST_POSITION;
    if (assetName && assetSourceType === CHARGING_STATION_TYPE) {
        return assetName
            .replace(/_([a-z])/g, (_, char) => ` ${char.toUpperCase()}`)
            .replace(/^./, (char) => char.toUpperCase());
    }
    else {
        return assetName ? `${assetName.substring(FIRST_POSITION, LAST_POSITION)}` : "";
    }
};
export const trimAssetName = (assetName, assetMetadata) => {
    if (!assetName)
        return "";
    // If make is DENS, show first two and last two characters
    if (assetMetadata?.make === DENS_MAKE && assetName.length >= 4) {
        return assetName.substring(0, 2) + assetName.substring(assetName.length - 2);
    }
    const updatedAssetName = assetName.split("/");
    return updatedAssetName.length ? updatedAssetName[0] : assetName;
};
/**
 * Determines if the last reported time is stale (older than 18 hours).
 * NOTE: The API provides timestamps already in the project's local timezone.
 *
 * @param lastReportedTime - Timestamp string in project's local timezone
 * @param projectTimezone - IANA timezone name (e.g., "Europe/London", "Europe/Amsterdam")
 * @returns true if the timestamp is stale or invalid, false otherwise
 */
/**
 * Checks if the last reported time is stale (more than 18 hours ago).
 *
 * CALCULATION FUNCTION - Uses fromZonedTime approach (see TIMEZONE_ARCHITECTURE.md)
 *
 * Similar to isAssetReporting(), this needs accurate UTC milliseconds for comparison.
 * All users must get the same result regardless of their browser's timezone.
 */
export const isLastReportedTimeStale = (lastReportedTime, projectTimezone) => {
    const EIGHTEEN = 18;
    if (!lastReportedTime) {
        return true;
    }
    try {
        // Special handling for time-only format (e.g., "19:00:00")
        // Append today's date in the project's timezone to construct a full timestamp
        if (isTimeOnlyTimestamp(lastReportedTime)) {
            if (!projectTimezone || !isValidIANATimezone(projectTimezone)) {
                // Cannot determine staleness without valid timezone, assume stale
                return true;
            }
            // Get today's date in the project's timezone (e.g., "2025-12-18" in Europe/Amsterdam)
            const todayInProjectTz = formatInTimeZone(new Date(), projectTimezone, "yyyy-MM-dd");
            // Construct full timestamp: "2025-12-18T19:00:00"
            const fullTimestamp = `${todayInProjectTz}T${lastReportedTime}`;
            // Parse as project timezone and convert to UTC for comparison
            const lastReportedDate = fromZonedTime(fullTimestamp, projectTimezone);
            if (isNaN(lastReportedDate.getTime())) {
                return true;
            }
            const currentTime = new Date();
            const hoursDifference = differenceInHours(currentTime, lastReportedDate);
            return hoursDifference > EIGHTEEN;
        }
        // API provides timestamps in project's local timezone (e.g., "2025-12-17T20:46:00.000" = 20:46 London time)
        // We interpret this timestamp AS IF it's in the project's timezone, then convert to UTC for comparison
        let lastReportedDate;
        if (projectTimezone && isValidIANATimezone(projectTimezone)) {
            // Parse the timestamp string as a date in the project's timezone, convert to UTC
            // Returns a Date object with correct UTC milliseconds for reliable comparison
            lastReportedDate = fromZonedTime(lastReportedTime, projectTimezone);
        }
        else {
            // Fallback: treat as UTC if no timezone provided
            const timeAsUTC = lastReportedTime.endsWith("Z") ? lastReportedTime : `${lastReportedTime}Z`;
            lastReportedDate = new Date(timeAsUTC);
        }
        if (isNaN(lastReportedDate.getTime())) {
            return true;
        }
        const currentTime = new Date();
        const hoursDifference = differenceInHours(currentTime, lastReportedDate);
        return hoursDifference > EIGHTEEN;
    }
    catch (error) {
        // If any error occurs during parsing, consider the time as stale
        return true;
    }
};
export const getAssetImageIcon = (assetDetails) => {
    const productFamilyIconUrl = assetDetails?.assetMetadata?.productFamily?.iconUrl || assetDetails?.productFamily?.iconUrl;
    if (productFamilyIconUrl?.trim()) {
        return productFamilyIconUrl.trim();
    }
    if (assetDetails?.assetId === "9fd64572-d32a-4473-951a-5d16a053646e" ||
        assetDetails?.assetId === "5fe212ea-8fa8-4d71-a020-d3e4b504ee3a" ||
        assetDetails?.assetId === "0fa9aedd-8b63-4436-b17f-f88fc4994290") {
        return cat950Icon;
    }
    else if (assetDetails?.assetId === "815dc59c-3819-4a77-95a5-2397c6321060" ||
        assetDetails?.assetId === "037e557d-8d19-4680-a958-c1845affbbb9") {
        return xosHubIcon;
    }
    else if (assetDetails?.assetMetadata?.make === PSW_MAKE) {
        return pswIcon;
    }
    else if (assetDetails?.assetMetadata?.model === MODEL_950GC &&
        assetDetails?.assetMetadata?.productFamily?.code === MDWL_PRODUCT_FAMILY) {
        return MediumWheelLoaderIcon;
    }
    else if (assetDetails?.assetSourceType === ESS_TYPE &&
        assetDetails?.assetMetadata?.productFamily?.code === BAB_MAKE) {
        return bigAssBattery;
    }
    else if (assetDetails?.assetSourceType === ESS_TYPE &&
        assetDetails?.assetMetadata?.productFamily?.code === DENS_MAKE) {
        return densBattery;
    }
    else if (assetDetails?.assetSourceType?.toUpperCase() === METHANOL_REFORMER_TYPE) {
        return methanolReformerIcon;
    }
    else if (assetDetails?.assetSourceType?.toUpperCase() === "GHOST") {
        return unknownMachineIcon;
    }
    else if (assetDetails?.assetSourceType?.toUpperCase() === CHARGER_TYPE) {
        return chargerIcon;
    }
    return defaultMarkerIcon;
};
const getLabels = (activities, labelToSourceType = {}) => {
    const uniqueLabels = [];
    const chargerLabels = [];
    const fixedLabels = [];
    const activityLabels = [];
    if (!activities || activities.length < 1)
        return [];
    activities.forEach((activity) => {
        const chargeAsset = formatAssetLabelForSankey(activity.assetName);
        const dischargeAsset = formatAssetLabelForSankey(activity.dischargeAssetName);
        if (!uniqueLabels.includes(chargeAsset))
            uniqueLabels.push(chargeAsset);
        if (!uniqueLabels.includes(dischargeAsset))
            uniqueLabels.push(dischargeAsset);
    });
    uniqueLabels.forEach((label) => {
        if (labelToSourceType[label]?.toUpperCase() === CHARGER_TYPE) {
            // CHARGER nodes appear at the top of the left column, above WATTHUB
            chargerLabels.push(label);
        }
        else if (label === "WATTHUB" || label?.includes("Residual")) {
            fixedLabels.push(label);
        }
        else {
            activityLabels.push(label);
        }
    });
    //updatedLabels: CHARGERs first, then WATTHUB/Residual, then the rest
    return chargerLabels.concat(fixedLabels).concat(activityLabels);
};
const getSankeyChartLabels = (activities, labelToSourceType = {}) => {
    const uniqueLabels = [];
    const chargerLabels = [];
    const otherLabels = [];
    if (!activities || activities.length < 1)
        return [];
    activities.forEach((activity) => {
        const chargeAsset = formatAssetLabelForSankey(activity.assetName);
        const dischargeAsset = formatAssetLabelForSankey(activity.dischargeAssetName);
        if (!uniqueLabels.includes(chargeAsset))
            uniqueLabels.push(chargeAsset);
        if (!uniqueLabels.includes(dischargeAsset))
            uniqueLabels.push(dischargeAsset);
    });
    // Hoist CHARGER labels before WATTHUB so D3 stacks them at the top of column 0
    uniqueLabels.forEach((label) => {
        if (labelToSourceType[label]?.toUpperCase() === CHARGER_TYPE) {
            chargerLabels.push(label);
        }
        else {
            otherLabels.push(label);
        }
    });
    return chargerLabels.concat(otherLabels);
};
const getLinks = (activities, labels) => {
    if (!activities || activities.length < 1)
        return { source: [], target: [], value: [] };
    const source = activities.map((activity) => labels.findIndex((label) => label === formatAssetLabelForSankey(activity.assetName)));
    const target = activities.map((activity) => labels.findIndex((label) => label === formatAssetLabelForSankey(activity.dischargeAssetName)));
    const value = activities.map((activity) => activity.dischargeEnergy);
    return {
        source,
        target,
        value
    };
};
const getSankeyChartLinks = (activities) => {
    const links = [];
    activities.map((activity) => {
        links.push({
            source: formatAssetLabelForSankey(activity.assetName),
            target: formatAssetLabelForSankey(activity.dischargeAssetName),
            value: activity.dischargeEnergy ? parseFloat(activity.dischargeEnergy) : 0
        });
    });
    return links;
};
const formatAssetLabelForSankey = (label) => {
    // First, check if this asset has a special name mapping (e.g., PH10-24016 -> PH16)
    // This ensures Sankey diagrams show correct names like "PH16" and "PH19" instead of "PH10"
    if (ASSET_NAME_MAPPINGS[label]) {
        return ASSET_NAME_MAPPINGS[label];
    }
    // Default behavior: extract the prefix before special characters
    let words;
    if (label?.includes("(")) {
        words = label.split("(");
    }
    else {
        words = label?.split("-");
    }
    return words !== undefined ? words[0] : label;
};
const getPositions = (labels, labelToSourceType = {}) => {
    const x = [];
    const y = [];
    if (!labels || labels.length < 1)
        return { x: [], y: [] };
    let cy = 0.1;
    let ey = 0.0;
    let my = 0.1;
    let oy = 0.0;
    labels.map((label) => {
        // To fix any deviations of data, where the node would be rendered too far
        // from its ideal position. For now this is not needed, but leaving this
        // mapping there, if needed in future
        if (labelToSourceType[label]?.toUpperCase() === CHARGER_TYPE) {
            // CHARGER: left column (x=0.0), above WATTHUB (y < 0.5)
            x.push(0.0);
            y.push(cy);
            cy = cy + 0.1;
        }
        else if (label === "WATTHUB" || labelToSourceType[label]?.toUpperCase() === CHARGING_STATION_TYPE) {
            const wattHubXValue = 0.0;
            const wattHubYValue = 0.5;
            x.push(wattHubXValue);
            y.push(wattHubYValue);
        }
        else if (label.startsWith("1") ||
            label.startsWith("3") ||
            label.startsWith("4") ||
            label.includes("Other Machine") ||
            label.startsWith("Unknown")) {
            const otherMachineXValue = 0.4;
            x.push(otherMachineXValue);
            my = my + 0.1;
            y.push(my);
        }
        else if (label.startsWith("CQ") ||
            label.startsWith("BQ") ||
            label.startsWith("VO") ||
            label.startsWith("RL") ||
            label.startsWith("RO") ||
            label.includes("Other ESS")) {
            const otherESSValue = 0.2;
            x.push(otherESSValue);
            ey = ey + 0.1;
            y.push(ey);
        }
        else {
            x.push(1);
            oy = oy + 0.1;
            y.push(oy);
        }
    });
    return {
        x,
        y
    };
};
const getCustomData = (labels) => labels.map((label) => label.toString());
export const prepareSankeyData = (activities) => {
    const labelToSourceType = {};
    (activities || []).forEach((activity) => {
        const formatted = formatAssetLabelForSankey(activity.assetName);
        if (activity.assetSourceType)
            labelToSourceType[formatted] = activity.assetSourceType;
    });
    const labels = getLabels(activities, labelToSourceType);
    const links = getLinks(activities, labels);
    const positions = getPositions(labels, labelToSourceType);
    const customData = getCustomData(labels);
    return {
        customData,
        labels,
        links,
        positions
    };
};
const prepareSankeyChartNodes = (labels, activities = []) => {
    // Build a lookup: formatted label → assetSourceType from the raw activities
    const labelToSourceType = {};
    activities.forEach((activity) => {
        const formatted = formatAssetLabelForSankey(activity.assetName);
        if (activity.assetSourceType) {
            labelToSourceType[formatted] = activity.assetSourceType;
        }
    });
    const nodes = [];
    labels.map((label) => {
        // Methanol reformers: identified by label prefix (E_N_) or assetSourceType ENERGY_SOURCE
        if (label.startsWith("E_N_") || labelToSourceType[label]?.toUpperCase() === ENERGY_SOURCE_TYPE.toUpperCase()) {
            nodes.push({ id: label, type: "METHANOL_REFORMER" });
        }
        else if (labelToSourceType[label]?.toUpperCase() === CHARGER_TYPE) {
            // Classify as CHARGER type so it is mapped to column 0 (same as CHARGING_STATION).
            // Vertical ordering above WATTHUB is controlled by getLabels/getSankeyChartLabels
            // (which hoist CHARGER labels first) and getPositions (which assigns y < 0.5).
            nodes.push({ id: label, type: "CHARGER" });
        }
        else if (label.toUpperCase() === "WATTHUB" ||
            label === "Office Grid Connection" ||
            label === "Site Grid Connection" ||
            labelToSourceType[label]?.toUpperCase() === CHARGING_STATION_TYPE) {
            nodes.push({ id: label, type: "CHARGING_STATION" });
        }
        else if (label.startsWith("CQ") ||
            label.startsWith("BQ") ||
            label.startsWith("VO") ||
            label.startsWith("RL") ||
            label.startsWith("RO") ||
            label.startsWith("AC") ||
            label.startsWith("DC") ||
            label.startsWith("XES") ||
            label.startsWith("PH") || // Added PH prefix for ESS assets like PH19, PH16
            label.includes("Other ESS")) {
            nodes.push({ id: label, type: "ESS" });
        }
        else if (label.startsWith("1") ||
            label.startsWith("3") ||
            label.startsWith("4") ||
            label.startsWith("9") ||
            label.includes("Other Machine") ||
            label.toLowerCase().startsWith("unknown")) {
            nodes.push({ id: label, type: "MACHINE" });
        }
        else if (label.includes("Residual") || label.includes("Energy Loss")) {
            nodes.push({ id: label, type: "RESIDUAL" });
        }
        else {
            nodes.push({ id: label, type: "OTHER" });
        }
    });
    return nodes;
};
export const prepareSankeyChartData = (activities, showMethanolReformer = false, showMecCharger = false) => {
    // Filter out if dischargeAssetName is No Activity logged
    activities = activities.filter((activity) => activity.dischargeAssetName !== "No Activity logged");
    const formatAssetName = (name) => {
        if (name.toLowerCase() === "office_grid_connection") {
            return "Office Grid Connection";
        }
        else if (name.toLowerCase() === "site_grid_connection") {
            return "Site Grid Connection";
        }
        else {
            return name;
        }
    };
    activities.forEach((activity) => {
        activity.assetName = formatAssetName(activity.assetName);
        activity.dischargeAssetName = formatAssetName(activity.dischargeAssetName);
    });
    // Build a shared labelToSourceType lookup used by labels, links and node classification
    const labelToSourceType = {};
    activities.forEach((activity) => {
        const formatted = formatAssetLabelForSankey(activity.assetName);
        if (activity.assetSourceType)
            labelToSourceType[formatted] = activity.assetSourceType;
    });
    const labels = getSankeyChartLabels(activities, labelToSourceType);
    const links = getSankeyChartLinks(activities);
    // Pass activities so nodes can resolve assetSourceType for ENERGY_SOURCE classification
    const nodes = prepareSankeyChartNodes(labels, activities);
    const filteredNodeList = showMethanolReformer ? nodes : nodes.filter((node) => node.type !== "METHANOL_REFORMER");
    const filteredNodeIdList = new Set(filteredNodeList.map((node) => node.id));
    const filteredLinkList = links.filter((link) => filteredNodeIdList.has(link.source) && filteredNodeIdList.has(link.target));
    // Multiple types share column 0 so D3 stacks them vertically on the left side.
    const TYPE_TO_COLUMN = {
        CHARGER: 0,
        CHARGING_STATION: 0,
        ESS: 1,
        MACHINE: 2,
        ...(showMethanolReformer ? { METHANOL_REFORMER: 0 } : {}),
        ...(showMecCharger ? { CHARGER: 0 } : {}),
        OTHER: 3,
        RESIDUAL: 2
    };
    const SVG_WIDTH = SANKEY_SVG_WIDTH;
    const NODE_WIDTH = SANKEY_NODE_WIDTH;
    const PADDING = 60;
    const presentColumns = [...new Set(filteredNodeList.map((node) => TYPE_TO_COLUMN[node.type]))].sort((a, b) => a - b);
    const columnCount = presentColumns.length;
    const availableWidth = SVG_WIDTH - 2 * PADDING - NODE_WIDTH;
    const spacing = columnCount > 1 ? availableWidth / (columnCount - 1) : 0;
    filteredNodeList.forEach((node) => {
        const column = TYPE_TO_COLUMN[node.type];
        const columnIndex = presentColumns.indexOf(column);
        node.xPos = PADDING + columnIndex * spacing;
    });
    return {
        links: filteredLinkList,
        nodes: filteredNodeList
    };
};
export const extractHourlyEnergyData = (energyUsageHistory) => {
    const hourlyUsage = [];
    if (energyUsageHistory && energyUsageHistory.energyHistogram && energyUsageHistory.energyHistogram["hourly"]) {
        energyUsageHistory.energyHistogram["hourly"].map((usage) => {
            hourlyUsage.push(Math.round(usage.value));
        });
    }
    return hourlyUsage;
};
export const getLocalTime = (utc, offset) => {
    if (isNotEmpty(utc) && isNotEmpty(offset)) {
        // Below if condition for test site where date format is not in ISO format
        if (isTimeOnlyTimestamp(utc)) {
            utc = new Date().toISOString().split("T")[0] + "T" + utc;
        }
        const words = offset.split("UTC");
        const offsetTime = parseInt(words[1]);
        const utcDate = new Date(utc);
        if (isNaN(utcDate.valueOf())) {
            return null;
        }
        const MILLI_SECONDS = 3600000;
        return new Date(utcDate.getTime() + offsetTime * MILLI_SECONDS);
    }
    return null;
};
function isNotEmpty(str) {
    return typeof str === "string" && str.trim() !== "";
}
export var AssetTransitDirection;
(function (AssetTransitDirection) {
    AssetTransitDirection["AT_SITE"] = "ATSITE";
    AssetTransitDirection["AT_SOURCE"] = "ATSOURCE";
    AssetTransitDirection["TOWARDS_SITE"] = "TOWARDSSITE";
    AssetTransitDirection["TOWARDS_SOURCE"] = "TOWARDSSOURCE";
})(AssetTransitDirection || (AssetTransitDirection = {}));
export const getSankeyNodeColor = (type) => {
    switch (type) {
        case "ESS":
            return "#CAE5C7";
        case "MACHINE":
            return "#FFE672";
        case "CHARGING_STATION":
            return "#9E90D5";
        case "CHARGER":
            return "#4DB6E8";
        case "METHANOL_REFORMER":
            // Teal (#1AC69E) — matches Figma spec for Methanol Solution node color
            return "#1AC69E";
        case "RESIDUAL":
            return "#FB5A00";
        default:
            return "#B1E3E3";
    }
};
export const extractDailyEnergyData = (energyUsageHistory) => {
    const formattedChargeEnergy = [], formattedDischargeEnergy = [], formattedDate = [];
    let totalDischarge = 0;
    if (energyUsageHistory &&
        energyUsageHistory.energyHistogram &&
        energyUsageHistory.energyHistogram["dailyWiseMonth"]) {
        energyUsageHistory.energyHistogram["dailyWiseMonth"].forEach((usage) => {
            const { chargeEnergy = 0, date = "", dischargeEnergy = 0 } = usage;
            let formattedDateStr = "";
            try {
                const parsedDate = new Date(date);
                if (isNaN(parsedDate.getTime())) {
                    throw new Error(`Invalid date: ${date}`);
                }
                formattedDateStr = format(parsedDate, "dd-MMM");
            }
            catch (error) {
                console.error("Error formatting date:", error);
                // Fallback: try to extract date parts manually
                if (date && typeof date === "string") {
                    const dateParts = date.split(/[-T]/);
                    if (dateParts.length >= 3) {
                        const dayStr = dateParts[2];
                        const monthStr = dateParts[1];
                        // Validate day and month values
                        const dayNum = parseInt(dayStr, 10);
                        const monthIndex = parseInt(monthStr, 10) - 1;
                        // Check if parsed values are valid numbers within expected ranges
                        const isDayValid = !isNaN(dayNum) && dayNum >= 1 && dayNum <= 31;
                        const isMonthValid = !isNaN(monthIndex) && monthIndex >= 0 && monthIndex <= 11;
                        if (isDayValid && isMonthValid) {
                            const monthName = MONTH_NAMES[monthIndex];
                            formattedDateStr = `${dayStr.padStart(2, "0")}-${monthName}`;
                        }
                        else {
                            // Invalid day or month, fall back to substring
                            formattedDateStr = date.substring(0, 10);
                        }
                    }
                    else {
                        formattedDateStr = date.substring(0, 10);
                    }
                }
                else {
                    formattedDateStr = "N/A";
                }
            }
            formattedChargeEnergy.push(Math.round(chargeEnergy));
            formattedDischargeEnergy.push(Math.round(dischargeEnergy));
            formattedDate.push(formattedDateStr);
            totalDischarge += dischargeEnergy;
        });
    }
    return {
        formattedChargeEnergy,
        formattedDate,
        formattedDischargeEnergy,
        totalDischarge
    };
};
export const extractDailyChargerEnergyData = (energyUsageHistory) => {
    const formattedChargeEnergy = [], formattedDischargeEnergy = [], formattedDate = [];
    let totalDischarge = 0;
    if (energyUsageHistory &&
        energyUsageHistory.energyHistogram &&
        energyUsageHistory.energyHistogram["dailyWiseMonth"]) {
        energyUsageHistory.energyHistogram["dailyWiseMonth"].forEach((usage) => {
            const { chargeEnergy = 0, date = "", dischargeEnergy = 0 } = usage;
            formattedChargeEnergy.push(Math.round(chargeEnergy));
            formattedDischargeEnergy.push(Math.round(dischargeEnergy));
            formattedDate.push(date);
            totalDischarge += dischargeEnergy;
        });
    }
    return {
        formattedChargeEnergy,
        formattedDate,
        formattedDischargeEnergy,
        totalDischarge
    };
};
/**
 * Formats a timestamp string for display.
 * NOTE: The API already provides timestamps in the project's local timezone.
 * This function displays the timestamp AS-IS without timezone conversion.
 *
 * IMPORTANT: For DISPLAY purposes only. For calculations (like determining if an asset
 * is reporting), use isAssetReporting() or isLastReportedTimeStale() which properly
 * interpret the timestamp using fromZonedTime().
 *
 * @param localDate - Timestamp string already in the project's local timezone (e.g., "2025-12-09T11:27:00.000")
 * @param formatStr - Optional format string for date-fns format function (default: "dd MMM yyy HH:mm")
 * @param _projectOffset - Optional offset string (kept for backward compatibility, not used)
 * @param _projectTimezone - Optional timezone name (kept for backward compatibility, not used)
 * @returns Formatted timestamp string, or null if input is invalid
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
/**
 * Formats a timestamp for display in the UI.
 *
 * DISPLAY FUNCTION - Uses Z-Appending approach (see TIMEZONE_ARCHITECTURE.md)
 *
 * NOTE: The API provides timestamps already in the project's local timezone.
 * This function ensures ALL users globally see the SAME wall-clock time.
 *
 * Why Z-appending instead of fromZonedTime:
 *   - We want to show "20:46" to everyone (Tokyo, New York, London users)
 *   - Z-appending prevents browser timezone conversion
 *   - formatInTimeZone with UTC keeps the original time values intact
 *   - fromZonedTime would give us correct UTC, but format() would convert to browser timezone
 *
 * Example:
 *   Input: "2025-12-17T20:46:00.000" (London time)
 *   Tokyo user → sees "20:46 GMT"
 *   New York user → sees "20:46 GMT"
 *   London user → sees "20:46 GMT"
 *
 * @param localDate - Timestamp string in project's local timezone
 * @param formatStr - Optional date format string (default: "dd MMM yyy HH:mm")
 * @param _projectOffset - Deprecated, kept for backward compatibility
 * @param _projectTimezone - Deprecated, kept for backward compatibility
 * @returns Formatted date string, or null if invalid
 */
export const getCESTLocalTime = (localDate, formatStr = "", 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
_projectOffset, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
_projectTimezone) => {
    if (isNotEmpty(localDate)) {
        // Handle legacy time-only format (HH:mm:ss) by prepending today's date
        // NOTE: Some project API always returns timestamps as time-only format (e.g., "07:00:00")
        // This is a known limitation of the those specific data sources
        // For display, components should detect this format and show "time only" to users
        if (localDate.indexOf("T") === -1 && localDate.indexOf("-") === -1) {
            if (isTimeOnlyTimestamp(localDate)) {
                // Prepend today's date as fallback for formatting purposes
                // Note: This loses actual historical date information
                localDate = new Date().toISOString().split("T")[0] + "T" + localDate + ".000";
            }
        }
        const dateFormat = formatStr ? formatStr : "dd MMM yyy HH:mm";
        try {
            // API provides timestamps in project's local timezone (e.g., "2025-12-17T20:46:00.000" = 20:46 Amsterdam time)
            // For DISPLAY purposes, we want to show this EXACT time to all users (no conversion)
            // We append 'Z' to treat the timestamp as UTC, then format in UTC timezone
            // This prevents any browser timezone conversion while preserving the original time values
            const timeAsUTC = localDate.endsWith("Z") ? localDate : `${localDate}Z`;
            const parsedDate = new Date(timeAsUTC);
            // Check if the date is valid
            if (isNaN(parsedDate.getTime())) {
                return null;
            }
            // Format using formatInTimeZone with UTC to force display without timezone conversion
            // This ensures all users globally see the same time values (the project's local time)
            return formatInTimeZone(parsedDate, "UTC", dateFormat);
        }
        catch (error) {
            console.error("Error formatting date:", error);
            return null;
        }
    }
    return null;
};
/**
 * Formats last reported timestamp with timezone-aware display.
 * Shared helper for pages/drawers that need the AssetPageDetail formatting behavior.
 */
export const formatLastReportedTime = (time, timezone, showCestTimezone) => {
    if (!time)
        return "";
    const { formattedTime, timezoneAbbr } = formatTimestampWithTimezone(time, timezone, "us-date", (ts) => {
        if (showCestTimezone && !ts.endsWith("Z")) {
            try {
                const [datePart, timePart] = ts.split("T");
                if (!datePart || !timePart)
                    return "";
                const [year, month, day] = datePart.split("-");
                const [hour, minute] = timePart.split(":");
                return `${month}/${day}/${year}; ${hour}:${minute}`;
            }
            catch (error) {
                return "";
            }
        }
        return getCESTLocalTime(ts, "MM/dd/yyyy; HH:mm") || "";
    });
    return timezoneAbbr ? `${formattedTime} ${timezoneAbbr}` : formattedTime || "";
};
